import React, { useMemo } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  Icon,
  Separator,
  Text,
  type IconName,
} from "@salestrak/ui";

import type { MapPin } from "./map-types";
import { InfoboxIssueCard } from "./infobox-issue-card";
import type { MapIssue } from "./map-issue";
import type { AddressDetails } from "#app/lib/hooks/use-map-infobox-data";

// Snap points are fractions of the viewport, not percentage strings.
const SNAP_POINTS = [0.65];

const sqftFormatter = new Intl.NumberFormat();
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

type DetailRow = { icon: IconName; label: string; value: string };

function propertyRowsFromPin(pin: MapPin): DetailRow[] {
  const d = pin.propertyDetails;
  const rows: Array<DetailRow | null> = [
    d?.squareFootage
      ? { icon: "maximize-2", label: "Square Feet", value: `${sqftFormatter.format(d.squareFootage)} sqft` }
      : null,
    d?.yearBuilt
      ? { icon: "home", label: "Year Built", value: String(d.yearBuilt) }
      : null,
    d?.ownerNames?.[0]
      ? { icon: "user", label: "Owner", value: d.ownerNames[0] }
      : null,
    d?.marketValue
      ? { icon: "building", label: "Estimated Value", value: currencyFormatter.format(d.marketValue) }
      : null,
  ];
  return rows.filter((r): r is DetailRow => r !== null);
}

function buildSubtitle(
  pin: MapPin,
  addressDetails: AddressDetails | null,
): string | undefined {
  const city = addressDetails?.city ?? pin.city;
  const state = addressDetails?.stateProvince ?? pin.stateProvince;
  const postal = addressDetails?.postalCode ?? pin.postalCode;
  const joined = [city, state, postal].filter(Boolean).join(", ");
  if (!joined) return undefined;
  return joined.replace(/, (\d)/, " $1");
}

interface MapInfoboxSheetProps {
  pin: MapPin | null;
  issues: MapIssue[];
  addressDetails: AddressDetails | null;
  onDismiss: () => void;
  onIssueClick: (issue: MapIssue) => void;
  onActivityClick: (issue: MapIssue) => void;
  onAddProject: () => void;
}

/**
 * The mobile map infobox. `Drawer` is state-driven, so presence IS
 * `pin != null` — the caller holds no ref and there is nothing to present or
 * dismiss imperatively. `DrawerPopup` owns the bottom safe area.
 */
export function MapInfoboxSheet({
  pin,
  issues,
  addressDetails,
  onDismiss,
  onIssueClick,
  onActivityClick,
  onAddProject,
}: MapInfoboxSheetProps) {
  // Retain the last non-null pin so the dismiss animation plays with real
  // content after the parent clears selectedPin.
  const lastPinRef = React.useRef<MapPin | null>(pin);
  if (pin) lastPinRef.current = pin;
  const renderPin = pin ?? lastPinRef.current;

  const hasIssues = (renderPin?.issueIds.length ?? 0) > 0;
  const title = renderPin
    ? addressDetails?.addressLine1 ?? renderPin.addressLine1 ?? "Map pin"
    : "";
  const subtitle = renderPin ? buildSubtitle(renderPin, addressDetails) : undefined;
  const propertyRows = useMemo(
    () => (renderPin ? propertyRowsFromPin(renderPin) : []),
    [renderPin],
  );

  return (
    <Drawer
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
      open={pin != null}
      snapPoints={SNAP_POINTS}
    >
      <DrawerPopup showBar>
        <DrawerHeader>
          <Box align="start" direction="row" gap="sm" justify="between">
            <Box gap="2xs" shrink>
              <DrawerTitle>{title}</DrawerTitle>
              {subtitle ? (
                <Text size="sm" variant="muted">
                  {subtitle}
                </Text>
              ) : null}
            </Box>
            <Button
              aria-label="Add project"
              className="rounded-full"
              onClick={onAddProject}
              size="icon-xs"
              variant="outline"
            >
              <Icon name="plus" size="xs" />
            </Button>
          </Box>
        </DrawerHeader>
        <DrawerPanel>
          {hasIssues ? (
            // id: mirrors the desktop overlay's e2e locator hook.
            <Box gap="xs" id="map-infobox" padding="sm">
              {issues.map((issue) => (
                <InfoboxIssueCard
                  key={issue.issueId}
                  issue={issue}
                  onActivityClick={onActivityClick}
                  onClick={onIssueClick}
                />
              ))}
            </Box>
          ) : propertyRows.length > 0 ? (
            <Box>
              {propertyRows.map((row, i) => (
                <React.Fragment key={row.label}>
                  {i > 0 && <Separator />}
                  <Box
                    align="center"
                    direction="row"
                    gap="sm"
                    paddingX="md"
                    paddingY="sm"
                  >
                    <Icon color="muted" name={row.icon} size="xs" />
                    <Box align="center" direction="row" fill="flex" justify="between">
                      <Text size="sm" variant="muted">
                        {row.label}
                      </Text>
                      <Text size="sm">{row.value}</Text>
                    </Box>
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          ) : (
            <Box align="center" paddingX="md" paddingY="lg">
              <Text size="sm" variant="muted">
                No property details available
              </Text>
            </Box>
          )}
        </DrawerPanel>
      </DrawerPopup>
    </Drawer>
  );
}
